import { countries, COUNTRY_CODES } from "@redotech/locale/countries";
import { USA_PROVINCE_CODES, usaProvinces } from "@redotech/locale/provinces";
import { z } from "zod";
import { zExt } from "../common/zod-util";

export enum CheckoutTreeNodeType {
  SPLIT_TEST = "SPLIT_TEST",
  CHECKOUT_EXPERIENCE = "CHECKOUT_EXPERIENCE",
  SEGMENT = "SEGMENT",
}

// Checkout experience node
const CheckoutTreeCheckoutExperienceNodeSchema = z.object({
  nodeType: z.literal(CheckoutTreeNodeType.CHECKOUT_EXPERIENCE),
  checkoutExperienceId: zExt.objectId(),
  _id: zExt.objectId().optional(),
}) as z.ZodType<CheckoutTreeCheckoutExperienceNode>;
export interface CheckoutTreeCheckoutExperienceNode {
  nodeType: CheckoutTreeNodeType.CHECKOUT_EXPERIENCE;
  checkoutExperienceId: string;
  _id?: string;
}

// Split test node
const CheckoutTreeSplitTestEdgeSchema = z.interface({
  get childNode() {
    return CheckoutTreeNodeZodSchema;
  },
  treatmentId: zExt.objectId(),
}) as z.ZodType<CheckoutTreeSplitTestEdge>;
export interface CheckoutTreeSplitTestEdge {
  childNode: CheckoutTreeNode;
  treatmentId: string;
}

const CheckoutTreeSplitTestNodeSchema = z.interface({
  nodeType: z.literal(CheckoutTreeNodeType.SPLIT_TEST),
  splitTestId: zExt.objectId(),
  edges: z.array(CheckoutTreeSplitTestEdgeSchema),
  _id: zExt.objectId().optional(),
}) as z.ZodType<CheckoutTreeSplitTestNode>;
export interface CheckoutTreeSplitTestNode {
  nodeType: CheckoutTreeNodeType.SPLIT_TEST;
  splitTestId: string;
  edges: CheckoutTreeSplitTestEdge[];
  _id?: string;
}

export function isCheckoutTreeSplitTestNode(
  node: CheckoutTreeNode,
): node is CheckoutTreeSplitTestNode {
  return node.nodeType === CheckoutTreeNodeType.SPLIT_TEST;
}

// Segment node
export enum SegmentableCheckoutCharacteristic {
  /**
   * The distance between the destination address and the fulfillment center.
   */
  DELIVERY_DISTANCE_MILES = "DELIVERY_DISTANCE_MILES",
  /**
   * Number of customer's previous orders.
   */
  ORDER_COUNT = "ORDER_COUNT",
  /**
   * The location of the delivery address.
   */
  DELIVERY_LOCATION = "DELIVERY_LOCATION",
  /**
   * Customer's tags.
   */
  CUSTOMER_TAGS = "CUSTOMER_TAGS",
  /**
   * Product tags.
   */
  PRODUCT_TAGS = "PRODUCT_TAGS",
  /**
   * Product vendor.
   */
  PRODUCT_VENDOR = "PRODUCT_VENDOR",
  /**
   * Product SKU.
   */
  PRODUCT_SKU = "PRODUCT_SKU",
  /**
   * Quantity of items in the cart (excluding products with SKU "x-redo").
   */
  ITEM_QUANTITY = "ITEM_QUANTITY",
}

export enum SegmentNumberOperator {
  LESS_THAN = "LESS_THAN",
  LESS_THAN_OR_EQUAL = "LESS_THAN_OR_EQUAL",
  EQUAL = "EQUAL",
}

export enum SegmentListSingleItemMembershipOperator {
  IN = "IN",
  NOT_IN = "NOT_IN",
}

export enum SegmentListMultipleItemMembershipOperator {
  ALL_IN = "ALL_IN",
  ANY_IN = "ANY_IN",
  NONE_IN = "NONE_IN",
}

const CheckoutTreeSegmentEdgeSchema = z.interface({
  get childNode() {
    return CheckoutTreeNodeZodSchema;
  },
}) as z.ZodType<CheckoutTreeSegmentEdge>;
export interface CheckoutTreeSegmentEdge {
  childNode: CheckoutTreeNode;
}

// Delivery distance miles segment
export const DeliveryDistanceMilesSegmentSchema = z.object({
  segmentableCharacteristic: z.literal(
    SegmentableCheckoutCharacteristic.DELIVERY_DISTANCE_MILES,
  ),
  miles: z.number(),
  operator: z.enum(SegmentNumberOperator),
});
export type DeliveryDistanceMilesSegment = z.infer<
  typeof DeliveryDistanceMilesSegmentSchema
>;
const DeliveryDistanceMilesSegmentDiscriminatedUnionOptionSchema =
  DeliveryDistanceMilesSegmentSchema;

export function isDeliveryDistanceMilesSegment(
  segment: Segment,
): segment is DeliveryDistanceMilesSegment {
  return (
    segment.segmentableCharacteristic ===
    SegmentableCheckoutCharacteristic.DELIVERY_DISTANCE_MILES
  );
}

// Order count segment
export const OrderCountSegmentSchema = z.object({
  segmentableCharacteristic: z.literal(
    SegmentableCheckoutCharacteristic.ORDER_COUNT,
  ),
  numOrders: z.number(),
  operator: z.enum(SegmentNumberOperator),
});
export type OrderCountSegment = z.infer<typeof OrderCountSegmentSchema>;
const OrderCountSegmentDiscriminatedUnionOptionSchema = OrderCountSegmentSchema;

export function isOrderCountSegment(
  segment: Segment,
): segment is OrderCountSegment {
  return (
    segment.segmentableCharacteristic ===
    SegmentableCheckoutCharacteristic.ORDER_COUNT
  );
}

// Delivery location segment
export const DeliveryLocationSegmentSchema = z.object({
  segmentableCharacteristic: z.literal(
    SegmentableCheckoutCharacteristic.DELIVERY_LOCATION,
  ),
  locationCountry: z.object({
    countries: z.array(
      z.enum(countries.map((c) => c.code) as [string, ...string[]]),
    ),
    operator: z.enum(SegmentListSingleItemMembershipOperator),
  }),
  // Right now, we only support USA provinces.
  locationProvince: z
    .object({
      provinces: z.array(
        z.enum(usaProvinces.map((p) => p.code) as [string, ...string[]]),
      ),
      operator: z.enum(SegmentListSingleItemMembershipOperator),
    })
    .optional(),
});
export type DeliveryLocationSegment = z.infer<
  typeof DeliveryLocationSegmentSchema
>;
const DeliveryLocationSegmentDiscriminatedUnionOptionSchema =
  DeliveryLocationSegmentSchema;

export function isDeliveryLocationSegment(
  segment: Segment,
): segment is DeliveryLocationSegment {
  return (
    segment.segmentableCharacteristic ===
    SegmentableCheckoutCharacteristic.DELIVERY_LOCATION
  );
}

// Customer tags segment
/**
 * Due to limitations in Shopify's delivery customization function, we can only
 * support this subset of multiple item list membership operators for customer
 * tags segments.
 */
const CustomerTagsSegmentListMultipleItemMembershipOperator = [
  SegmentListMultipleItemMembershipOperator.ANY_IN,
  SegmentListMultipleItemMembershipOperator.NONE_IN,
] as const;
type CustomerTagsSegmentListMultipleItemMembershipOperator =
  (typeof CustomerTagsSegmentListMultipleItemMembershipOperator)[number];

/**
 * Due to limitations in Shopify's delivery customization function, we only allow
 * two customer tags segments nodes in a checkout tree for now.
 */
export enum CustomerTagsSegmentNodeIdentifier {
  CUSTOMER_TAGS_SEGMENT_NODE_ONE = "CUSTOMER_TAGS_SEGMENT_NODE_ONE",
  CUSTOMER_TAGS_SEGMENT_NODE_TWO = "CUSTOMER_TAGS_SEGMENT_NODE_TWO",
}

export const CustomerTagsSegmentSchema = z.object({
  segmentableCharacteristic: z.literal(
    SegmentableCheckoutCharacteristic.CUSTOMER_TAGS,
  ),
  tags: z.array(z.string()),
  operator: z.enum(CustomerTagsSegmentListMultipleItemMembershipOperator),
  customerTagsSegmentNodeIdentifier: z.enum(CustomerTagsSegmentNodeIdentifier),
});
export type CustomerTagsSegment = z.infer<typeof CustomerTagsSegmentSchema>;
const CustomerTagsSegmentDiscriminatedUnionOptionSchema =
  CustomerTagsSegmentSchema;

export function isCustomerTagsSegment(
  segment: Segment,
): segment is CustomerTagsSegment {
  return (
    segment.segmentableCharacteristic ===
    SegmentableCheckoutCharacteristic.CUSTOMER_TAGS
  );
}

// Product tags segment
/**
 * Due to limitations in Shopify's delivery customization function, we can only
 * support this subset of multiple item list membership operators for product
 * tags segments.
 */
const ProductTagsSegmentListMultipleItemMembershipOperator = [
  SegmentListMultipleItemMembershipOperator.ANY_IN,
  SegmentListMultipleItemMembershipOperator.NONE_IN,
] as const;
type ProductTagsSegmentListMultipleItemMembershipOperator =
  (typeof ProductTagsSegmentListMultipleItemMembershipOperator)[number];

/**
 * Due to limitations in Shopify's delivery customization function, we only allow
 * two product tags segment nodes in a checkout tree for now.
 */
export enum ProductTagsSegmentNodeIdentifier {
  PRODUCT_TAGS_SEGMENT_NODE_ONE = "PRODUCT_TAGS_SEGMENT_NODE_ONE",
  PRODUCT_TAGS_SEGMENT_NODE_TWO = "PRODUCT_TAGS_SEGMENT_NODE_TWO",
}

export const ProductTagsSegmentSchema = z.object({
  segmentableCharacteristic: z.literal(
    SegmentableCheckoutCharacteristic.PRODUCT_TAGS,
  ),
  tags: z.array(z.string()),
  operator: z.enum(ProductTagsSegmentListMultipleItemMembershipOperator),
  cartOperator: z.enum(SegmentListMultipleItemMembershipOperator),
  productTagsSegmentNodeIdentifier: z.enum(ProductTagsSegmentNodeIdentifier),
});
export type ProductTagsSegment = z.infer<typeof ProductTagsSegmentSchema>;
const ProductTagsSegmentDiscriminatedUnionOptionSchema =
  ProductTagsSegmentSchema;

export function isProductTagsSegment(
  segment: Segment,
): segment is ProductTagsSegment {
  return (
    segment.segmentableCharacteristic ===
    SegmentableCheckoutCharacteristic.PRODUCT_TAGS
  );
}

// Product vendor segment
/**
 * Product vendors list with list membership operators for product vendor segments.
 */
export const ProductVendorSegmentSchema = z.object({
  segmentableCharacteristic: z.literal(
    SegmentableCheckoutCharacteristic.PRODUCT_VENDOR,
  ),
  vendors: z.array(z.string()),
  operator: z.enum(SegmentListSingleItemMembershipOperator),
  cartOperator: z.enum(SegmentListMultipleItemMembershipOperator),
});
export type ProductVendorSegment = z.infer<typeof ProductVendorSegmentSchema>;
const ProductVendorSegmentDiscriminatedUnionOptionSchema =
  ProductVendorSegmentSchema;

export function isProductVendorSegment(
  segment: Segment,
): segment is ProductVendorSegment {
  return (
    segment.segmentableCharacteristic ===
    SegmentableCheckoutCharacteristic.PRODUCT_VENDOR
  );
}

// Product SKU segment
/**
 * Product SKUs list with list membership operators for product SKU segments.
 */
export const ProductSkuSegmentSchema = z.object({
  segmentableCharacteristic: z.literal(
    SegmentableCheckoutCharacteristic.PRODUCT_SKU,
  ),
  skus: z.array(z.string()),
  operator: z.enum(SegmentListSingleItemMembershipOperator),
  cartOperator: z.enum(SegmentListMultipleItemMembershipOperator),
});
export type ProductSkuSegment = z.infer<typeof ProductSkuSegmentSchema>;
const ProductSkuSegmentDiscriminatedUnionOptionSchema = ProductSkuSegmentSchema;

export function isProductSkuSegment(
  segment: Segment,
): segment is ProductSkuSegment {
  return (
    segment.segmentableCharacteristic ===
    SegmentableCheckoutCharacteristic.PRODUCT_SKU
  );
}

// Item quantity segment
export const ItemQuantitySegmentSchema = z.object({
  segmentableCharacteristic: z.literal(
    SegmentableCheckoutCharacteristic.ITEM_QUANTITY,
  ),
  quantity: z.number(),
  operator: z.enum(SegmentNumberOperator),
  exclusions: z.object({ skus: z.array(z.string()).optional() }).optional(),
});
export type ItemQuantitySegment = z.infer<typeof ItemQuantitySegmentSchema>;
const ItemQuantitySegmentDiscriminatedUnionOptionSchema =
  ItemQuantitySegmentSchema;

export function isItemQuantitySegment(
  segment: Segment,
): segment is ItemQuantitySegment {
  return (
    segment.segmentableCharacteristic ===
    SegmentableCheckoutCharacteristic.ITEM_QUANTITY
  );
}

const SegmentSchema = z.discriminatedUnion("segmentableCharacteristic", [
  DeliveryDistanceMilesSegmentDiscriminatedUnionOptionSchema,
  OrderCountSegmentDiscriminatedUnionOptionSchema,
  DeliveryLocationSegmentDiscriminatedUnionOptionSchema,
  CustomerTagsSegmentDiscriminatedUnionOptionSchema,
  ProductTagsSegmentDiscriminatedUnionOptionSchema,
  ProductVendorSegmentDiscriminatedUnionOptionSchema,
  ProductSkuSegmentDiscriminatedUnionOptionSchema,
  ItemQuantitySegmentDiscriminatedUnionOptionSchema,
]);
type Segment =
  | DeliveryDistanceMilesSegment
  | OrderCountSegment
  | DeliveryLocationSegment
  | CustomerTagsSegment
  | ProductTagsSegment
  | ProductVendorSegment
  | ProductSkuSegment
  | ItemQuantitySegment;

const CheckoutTreeSegmentNodeSchema = z.object({
  nodeType: z.literal(CheckoutTreeNodeType.SEGMENT),
  matchingEdge: CheckoutTreeSegmentEdgeSchema,
  nonMatchingEdge: CheckoutTreeSegmentEdgeSchema,
  segment: SegmentSchema,
  _id: zExt.objectId().optional(),
});
export type CheckoutTreeSegmentNode = z.infer<
  typeof CheckoutTreeSegmentNodeSchema
>;

export function isCheckoutTreeSegmentNode(
  node: CheckoutTreeNode,
): node is CheckoutTreeSegmentNode {
  return node.nodeType === CheckoutTreeNodeType.SEGMENT;
}

// Checkout tree node
export const CheckoutTreeNodeZodSchema = z.union([
  CheckoutTreeCheckoutExperienceNodeSchema,
  CheckoutTreeSplitTestNodeSchema,
  CheckoutTreeSegmentNodeSchema,
]);
export type CheckoutTreeNode = z.infer<typeof CheckoutTreeNodeZodSchema>;

export const CheckoutTreeZodSchema = z.object({
  rootNode: CheckoutTreeNodeZodSchema,
});
export type CheckoutTree = z.infer<typeof CheckoutTreeZodSchema>;

export function parseCheckoutTree(checkoutTree: unknown): CheckoutTree {
  return CheckoutTreeZodSchema.parse(checkoutTree) as CheckoutTree;
}

export function safeParseCheckoutTree(
  checkoutTree: unknown,
): { success: true; value: CheckoutTree } | { success: false; error: Error } {
  const parsed = CheckoutTreeZodSchema.safeParse(checkoutTree);
  if (!parsed.success) {
    return { success: false, error: parsed.error };
  }
  return { success: true, value: parsed.data as CheckoutTree };
}

/**
 * Normally, we would use `zodResponseFormat` to auto generate the json schema
 * but it doesn't handle recursive zod structures well.
 *
 * So we manually define the schema here.
 */
export const checkoutTreeJsonSchema = {
  type: "object",
  properties: { rootNode: { $ref: "#/$defs/CheckoutTreeNode" } },
  additionalProperties: false,
  required: ["rootNode"],
  $defs: {
    CheckoutTreeNode: {
      anyOf: [
        { $ref: "#/$defs/CheckoutTreeCheckoutExperienceNode" },
        { $ref: "#/$defs/CheckoutTreeSplitTestNode" },
        { $ref: "#/$defs/CheckoutTreeSegmentNode" },
      ],
    },
    CheckoutTreeCheckoutExperienceNode: {
      type: "object",
      properties: {
        nodeType: { enum: ["CHECKOUT_EXPERIENCE"] },
        checkoutExperienceId: { type: "string" },
      },
      additionalProperties: false,
      required: ["nodeType", "checkoutExperienceId"],
    },
    CheckoutTreeSplitTestNode: {
      type: "object",
      properties: {
        nodeType: { enum: ["SPLIT_TEST"] },
        splitTestId: { type: "string" },
        edges: {
          type: "array",
          items: { $ref: "#/$defs/CheckoutTreeSplitTestEdge" },
        },
      },
      additionalProperties: false,
      required: ["nodeType", "splitTestId", "edges"],
    },
    CheckoutTreeSplitTestEdge: {
      type: "object",
      properties: {
        childNode: { $ref: "#/$defs/CheckoutTreeNode" },
        treatmentId: { type: "string" },
      },
      additionalProperties: false,
      required: ["childNode", "treatmentId"],
    },
    CheckoutTreeSegmentNode: {
      type: "object",
      properties: {
        nodeType: { enum: ["SEGMENT"] },
        matchingEdge: { $ref: "#/$defs/CheckoutTreeSegmentEdge" },
        nonMatchingEdge: { $ref: "#/$defs/CheckoutTreeSegmentEdge" },
        segment: { $ref: "#/$defs/Segment" },
      },
      additionalProperties: false,
      required: ["nodeType", "matchingEdge", "nonMatchingEdge", "segment"],
    },
    CheckoutTreeSegmentEdge: {
      type: "object",
      properties: { childNode: { $ref: "#/$defs/CheckoutTreeNode" } },
      additionalProperties: false,
      required: ["childNode"],
    },
    Segment: {
      anyOf: [
        { $ref: "#/$defs/OrderCountSegment" },
        { $ref: "#/$defs/DeliveryLocationSegment" },
        { $ref: "#/$defs/CustomerTagsSegment" },
        { $ref: "#/$defs/ProductTagsSegment" },
        { $ref: "#/$defs/ProductVendorSegment" },
        { $ref: "#/$defs/ItemQuantitySegment" },
      ],
    },
    ItemQuantitySegment: {
      type: "object",
      properties: {
        segmentableCharacteristic: { enum: ["ITEM_QUANTITY"] },
        quantity: { type: "number" },
        operator: { enum: ["LESS_THAN", "LESS_THAN_OR_EQUAL", "EQUAL"] },
        exclusions: {
          type: "object",
          properties: { skus: { type: "array", items: { type: "string" } } },
          additionalProperties: false,
        },
      },
      additionalProperties: false,
      required: ["segmentableCharacteristic", "quantity", "operator"],
    },
    OrderCountSegment: {
      type: "object",
      properties: {
        segmentableCharacteristic: { enum: ["ORDER_COUNT"] },
        numOrders: { type: "number" },
        operator: { enum: ["LESS_THAN", "LESS_THAN_OR_EQUAL", "EQUAL"] },
      },
      additionalProperties: false,
      required: ["segmentableCharacteristic", "numOrders", "operator"],
    },
    DeliveryLocationSegment_LocationCountry: {
      type: "object",
      properties: {
        countries: {
          type: "array",
          items: { type: "string", enum: COUNTRY_CODES },
        },
        operator: { enum: ["IN", "NOT_IN"] },
      },
      additionalProperties: false,
      required: ["countries", "operator"],
    },
    DeliveryLocationSegment_LocationProvince: {
      type: "object",
      properties: {
        provinces: {
          type: "array",
          items: { type: "string", enum: USA_PROVINCE_CODES },
        },
        operator: { enum: ["IN", "NOT_IN"] },
      },
      additionalProperties: false,
      required: ["provinces", "operator"],
    },
    DeliveryLocationSegment: {
      type: "object",
      properties: {
        segmentableCharacteristic: { enum: ["DELIVERY_LOCATION"] },
        locationCountry: {
          $ref: "#/$defs/DeliveryLocationSegment_LocationCountry",
        },
        locationProvince: {
          $ref: "#/$defs/DeliveryLocationSegment_LocationProvince",
        },
      },
      additionalProperties: false,
      required: [
        "segmentableCharacteristic",
        "locationCountry",
        "locationProvince",
      ],
    },
    CustomerTagsSegment: {
      type: "object",
      properties: {
        segmentableCharacteristic: { enum: ["CUSTOMER_TAGS"] },
        tags: { type: "array", items: { type: "string" } },
        operator: { enum: ["ANY_IN", "NONE_IN"] },
        customerTagsSegmentNodeIdentifier: {
          enum: [
            "CUSTOMER_TAGS_SEGMENT_NODE_ONE",
            "CUSTOMER_TAGS_SEGMENT_NODE_TWO",
          ],
        },
      },
      additionalProperties: false,
      required: [
        "segmentableCharacteristic",
        "tags",
        "operator",
        "customerTagsSegmentNodeIdentifier",
      ],
    },
    ProductTagsSegment: {
      type: "object",
      properties: {
        segmentableCharacteristic: { enum: ["PRODUCT_TAGS"] },
        tags: { type: "array", items: { type: "string" } },
        operator: { enum: ["ANY_IN", "NONE_IN"] },
        cartOperator: { enum: ["ALL_IN", "ANY_IN", "NONE_IN"] },
        productTagsSegmentNodeIdentifier: {
          enum: [
            "PRODUCT_TAGS_SEGMENT_NODE_ONE",
            "PRODUCT_TAGS_SEGMENT_NODE_TWO",
          ],
        },
      },
      additionalProperties: false,
      required: [
        "segmentableCharacteristic",
        "tags",
        "operator",
        "cartOperator",
        "productTagsSegmentNodeIdentifier",
      ],
    },
    ProductVendorSegment: {
      type: "object",
      properties: {
        segmentableCharacteristic: { enum: ["PRODUCT_VENDOR"] },
        vendors: { type: "array", items: { type: "string" } },
        operator: { enum: ["IN", "NOT_IN"] },
        cartOperator: { enum: ["ALL_IN", "ANY_IN", "NONE_IN"] },
      },
      additionalProperties: false,
      required: [
        "segmentableCharacteristic",
        "vendors",
        "operator",
        "cartOperator",
      ],
    },
  },
};
